import { getReferenceString, parseReference, resolveId } from "@medplum/core";
import { Encounter, EncounterParticipant, Practitioner, Reference } from "@medplum/fhirtypes";

import {
  EncounterPractitionerType,
  EncounterPractitionerTypeDisplay,
  QuiltedHealthCodeSystems,
} from "@/fhir/enums/quilted-health";
import { joinReferenceString } from "@/utils/medplum";

/**
 * Builds a map of participant references to display values from encounter participants.
 * Used when Practitioner reads fail (e.g. patient permissions) so we can fall back to
 * another participant row that carries the same reference with a display.
 */
export const buildReferenceDisplayMap = (encounter: Encounter): Map<string, string> => {
  const displayMap = new Map<string, string>();
  encounter.participant?.forEach((participant) => {
    if (participant.individual?.reference && participant.individual?.display) {
      displayMap.set(participant.individual.reference, participant.individual.display);
    }
  });
  return displayMap;
};

/**
 * Replaces the single `signed-and-locked-by` participant entry (there is exactly one per
 * encounter) and preserves all other rows unchanged.
 *
 * Only accepts `EncounterPractitionerType.SignedAndLockedBy`. Types like `provider`,
 * `birthing-assistant`, `doula`, and `others-present` are multi-instance — use
 * `appendEncounterParticipantIfAbsent` to add those without removing existing entries.
 */
export function upsertEncounterParticipantByType(
  encounter: Encounter,
  type: EncounterPractitionerType.SignedAndLockedBy,
  individual: Reference<Practitioner>,
): Encounter {
  const system = QuiltedHealthCodeSystems.EncounterPractitionerType;
  const display = EncounterPractitionerTypeDisplay[type];

  const filtered =
    encounter.participant?.filter(
      (participant) =>
        !participant.type?.some((participantTypeConcept) =>
          participantTypeConcept.coding?.some(
            (coding) => coding.system === system && coding.code === type,
          ),
        ),
    ) ?? [];

  const newEntry: EncounterParticipant = {
    type: [
      {
        coding: [{ system, code: type, display }],
      },
    ],
    individual,
  };

  return {
    ...encounter,
    participant: [...filtered, newEntry],
  };
}

/**
 * Appends a new participant entry only if no existing row already references the same individual
 * and type. Safe for multi-instance types (`provider`, `birthing-assistant`, `doula`,
 * `others-present`) where replacing would lose other entries.
 */
export function appendEncounterParticipantIfAbsent(
  encounter: Encounter,
  type: EncounterPractitionerType,
  individual: Reference<Practitioner>,
): Encounter {
  const system = QuiltedHealthCodeSystems.EncounterPractitionerType;
  const display = EncounterPractitionerTypeDisplay[type];

  const alreadyPresent = encounter.participant?.some(
    (participant) =>
      participant.individual?.reference === individual.reference &&
      participantHasType(participant, type),
  );

  if (alreadyPresent) return encounter;

  const newEntry: EncounterParticipant = {
    type: [{ coding: [{ system, code: type, display }] }],
    individual,
  };

  return {
    ...encounter,
    participant: [...(encounter.participant ?? []), newEntry],
  };
}

/**
 * Sets `status: finished`, optional `meta.author`, and upserts the `signed-and-locked-by`
 * participant. Also mirrors the signing practitioner's reference string (e.g.
 * `Practitioner/abc123`) onto a `meta.tag` of system `EncounterSignedAndLockedBy` so the
 * encounter can be searched by `_tag` for the provider who signed and locked it —
 * `Encounter.participant` has no FHIR search parameter that can target a specific participant
 * type.
 */
export function finishEncounterWithSignedAndLockedBy(
  encounter: Encounter,
  practitionerReference: Reference<Practitioner> | undefined,
): Encounter {
  let next: Encounter = {
    ...encounter,
    status: "finished",
    ...(practitionerReference
      ? {
          meta: {
            ...encounter.meta,
            author: practitionerReference,
          },
        }
      : {}),
  };

  if (practitionerReference?.reference?.startsWith("Practitioner/")) {
    next = upsertEncounterParticipantByType(
      next,
      EncounterPractitionerType.SignedAndLockedBy,
      practitionerReference,
    );

    const practitionerReferenceString = getReferenceString(practitionerReference);
    if (practitionerReferenceString) {
      const preservedTags = (next.meta?.tag ?? []).filter(
        (tag) => tag.system !== QuiltedHealthCodeSystems.EncounterSignedAndLockedBy,
      );
      next = {
        ...next,
        meta: {
          ...next.meta,
          tag: [
            ...preservedTags,
            {
              system: QuiltedHealthCodeSystems.EncounterSignedAndLockedBy,
              code: practitionerReferenceString,
            },
          ],
        },
      };
    }
  }

  return next;
}

/**
 * Practitioner id of the clinical provider participant (`EncounterPractitionerType.Provider`).
 * Independent of sign-and-lock display, which may name the signer instead.
 */
export function resolveEncounterClinicalProviderId(encounter: Encounter | undefined): string {
  if (!encounter) return "";
  const participant = findEncounterParticipantByType(encounter, EncounterPractitionerType.Provider);
  return resolveId(participant?.individual) ?? "";
}

export function findEncounterParticipantByType(
  encounter: Encounter,
  type: EncounterPractitionerType,
): EncounterParticipant | undefined {
  return encounter.participant?.find((participant) => participantHasType(participant, type));
}

/**
 * Returns true when the participant has a type coding whose code matches `code`.
 *
 * Code-only, so a coding from an unrelated system with the same code also matches. Prefer
 * `participantHasType` whenever the type is a `EncounterPractitionerType`.
 */
export function participantHasTypeCode(participant: EncounterParticipant, code: string): boolean {
  return (
    participant.type?.some((type) => type.coding?.some((coding) => coding.code === code)) ?? false
  );
}

/**
 * Returns true when the participant carries `type` under the Quilted Health practitioner-type
 * system. The single system-aware predicate every type lookup in this module shares, so reads and
 * writes agree on what counts as a `provider`, `signed-and-locked-by`, and so on.
 *
 * @param participant Participant whose type codings are inspected
 * @param type Quilted Health practitioner type to look for
 * @returns True when a coding matches both the Quilted Health system and `type`
 */
export function participantHasType(
  participant: EncounterParticipant,
  type: EncounterPractitionerType,
): boolean {
  const system = QuiltedHealthCodeSystems.EncounterPractitionerType;
  return (
    participant.type?.some((participantTypeConcept) =>
      participantTypeConcept.coding?.some(
        (coding) => coding.system === system && coding.code === type,
      ),
    ) ?? false
  );
}

/**
 * Whether the labor "Before charting" modal should open on first load.
 * Completed setup is indicated by a `before-charting` participant (or a legacy untyped
 * profile row saved before that type existed).
 */
export function shouldShowBeforeChartingModal(encounter: Encounter): boolean {
  if (findEncounterParticipantByType(encounter, EncounterPractitionerType.Participant)) {
    return false;
  }
  return true;
}

/**
 * Practitioner reference mirrored onto the `EncounterSignedAndLockedBy` `meta.tag` by
 * `finishEncounterWithSignedAndLockedBy`. Returns undefined when the tag is absent or its code is
 * not a well-formed `Practitioner/<id>` reference string.
 *
 * @param encounter Encounter whose signing practitioner is wanted
 * @returns Practitioner reference from the tag, or `undefined` when the tag is missing/malformed
 */
export function resolveEncounterSignedAndLockedByReference(
  encounter: Encounter | undefined,
): Reference<Practitioner> | undefined {
  const code = encounter?.meta?.tag?.find(
    (tag) => tag.system === QuiltedHealthCodeSystems.EncounterSignedAndLockedBy,
  )?.code;
  if (!code) return undefined;

  try {
    const [resourceType, resourceId] = parseReference({ reference: code });
    if (resourceType !== "Practitioner" || !resourceId) return undefined;
    return { reference: joinReferenceString(resourceType, resourceId) };
  } catch {
    return undefined;
  }
}
